import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import PaymentMethodSetup from "@/components/PaymentMethodSetup";
import { CLIENT_NAV } from "@/lib/clientNav";
import api from "@/lib/api";
import { ShieldCheck, Landmark, CreditCard, CheckCircle2, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

const NAV = CLIENT_NAV;

/**
 * Dedicated page /client/moyen-de-paiement — company can register a SEPA mandate
 * or a card BEFORE creating a devis. Simpler UX than the inline widget inside
 * step 5 of Configurator (which surprised users during the signature flow).
 */
export default function ClientPaymentSetup() {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const loadMethods = () => {
        setLoading(true);
        api.get("/company/payment-methods")
            .then(({ data }) => setMethods(data.methods || []))
            .catch(() => setMethods([]))
            .finally(() => setLoading(false));
    };
    useEffect(loadMethods, []);

    const removeMethod = async (id) => {
        if (!window.confirm("Supprimer ce moyen de paiement ?")) return;
        try {
            await api.delete(`/company/payment-methods/${id}`);
            toast.success("Moyen de paiement supprimé");
            loadMethods();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    const hasAny = methods.length > 0;

    return (
        <DashboardShell title="Espace client" nav={NAV}>
            <div className="mb-6">
                <button
                    onClick={() => navigate("/client")}
                    className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                    data-testid="pm-page-back"
                >
                    <ArrowLeft size={12} /> Retour au tableau de bord
                </button>
            </div>

            <div className="flex justify-between items-end flex-wrap gap-4 mb-8">
                <div>
                    <div className="overline mb-2 text-[#F5A623]">Paiement · SEPA / Carte</div>
                    <h1 className="font-display text-3xl md:text-4xl">
                        Enregistrez votre moyen de paiement
                    </h1>
                    <p className="text-sm text-slate-500 mt-2 max-w-xl">
                        Renforteo facture toutes vos missions en un seul prélèvement
                        automatique tous les 10 jours (cycles 1-10, 11-20, 21-fin de mois).
                        Aucun débit maintenant — juste un mandat SEPA ou une carte enregistrée
                        pour les cycles futurs.
                    </p>
                </div>
            </div>

            {/* Existing methods */}
            {loading ? (
                <div className="sd-card p-8 text-center text-sm text-slate-400" data-testid="pm-page-loading">
                    Chargement…
                </div>
            ) : hasAny ? (
                <section className="sd-card p-6 mb-8" data-testid="pm-page-existing">
                    <div className="overline mb-3">Moyens enregistrés</div>
                    <ul className="divide-y divide-slate-100">
                        {methods.map((m) => (
                            <li key={m.id} className="py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {m.type === "sepa_debit" ? (
                                        <Landmark size={20} className="text-[#0B192C]" />
                                    ) : (
                                        <CreditCard size={20} className="text-[#0B192C]" />
                                    )}
                                    <div>
                                        <div className="text-sm text-[#0B192C] font-semibold">
                                            {m.type === "sepa_debit"
                                                ? `Prélèvement SEPA · IBAN ····${m.last4}`
                                                : `${(m.brand || "CB").toUpperCase()} · ····${m.last4}`}
                                        </div>
                                        <div className="text-[11px] text-slate-400">
                                            {m.is_default ? "Par défaut · " : ""}
                                            Mandat {m.mandate_status}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {m.is_default && (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                                            <CheckCircle2 size={12} /> Actif
                                        </span>
                                    )}
                                    <button
                                        onClick={() => removeMethod(m.id)}
                                        className="text-xs text-slate-400 hover:text-red-700 inline-flex items-center gap-1"
                                        data-testid={`pm-remove-${m.id}`}
                                    >
                                        <Trash2 size={12} /> Supprimer
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : (
                <div
                    className="sd-card p-4 mb-8 border-l-4 border-red-500 bg-red-50/50"
                    data-testid="pm-page-empty-warning"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="text-red-600" size={16} />
                        <div className="text-sm font-semibold text-red-900">
                            Aucun moyen de paiement enregistré
                        </div>
                    </div>
                    <p className="text-xs text-slate-700">
                        Vous ne pourrez pas publier de mission tant qu'aucun mandat SEPA ou
                        carte n'est enregistré. Utilisez le formulaire ci-dessous.
                    </p>
                </div>
            )}

            {/* Setup form (add another method any time) */}
            <PaymentMethodSetup
                key={methods.length}
                onSuccess={() => {
                    loadMethods();
                    toast.success("Moyen de paiement enregistré · vous pouvez publier vos missions");
                }}
            />
        </DashboardShell>
    );
}
